import { apiRequest } from '../lib/api-client';
import type { BadgeTone } from '../ui/Page';

/** Agents, their versions, their runs and what they cost (context.md sections 87-99). */

export interface AgentView {
  id: string;
  name: string;
  description: string | null;
  role: string;
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
  published_version: number | null;
  channels: string[];
  created_at: string;
}

export interface VersionView {
  id: string;
  version: number;
  instructions: string;
  model: string;
  provider: string;
  tools: string[];
  approval_tools: string[];
  max_steps: number;
  content_hash: string;
  published_at: string | null;
}

export function listAgents(): Promise<{ agents: AgentView[] }> {
  return apiRequest('/v1/ai/agents');
}

export function getAgent(
  id: string,
): Promise<AgentView & { versions: VersionView[]; available_tools: string[] }> {
  return apiRequest(`/v1/ai/agents/${id}`);
}

export function createAgent(input: {
  name: string;
  description?: string | null;
  role?: string;
}): Promise<AgentView> {
  return apiRequest('/v1/ai/agents', { method: 'POST', body: input });
}

export function publishVersion(
  id: string,
  input: { instructions: string; tools?: string[]; approvalTools?: string[]; maxSteps?: number },
): Promise<VersionView> {
  return apiRequest(`/v1/ai/agents/${id}/versions`, { method: 'POST', body: input });
}

export function pauseAgent(id: string): Promise<AgentView> {
  return apiRequest(`/v1/ai/agents/${id}/pause`, { method: 'POST', body: {} });
}

export function resumeAgent(id: string): Promise<AgentView> {
  return apiRequest(`/v1/ai/agents/${id}/resume`, { method: 'POST', body: {} });
}

export interface RunResult {
  executionId: string;
  status: 'COMPLETED' | 'FAILED' | 'REFUSED' | 'STOPPED' | 'AWAITING_APPROVAL';
  approvalId?: string;
  output: string;
  steps: number;
  tokens: { prompt: number; completion: number };
  costCents: number;
}

export function runAgent(id: string, message: string): Promise<RunResult> {
  return apiRequest(`/v1/ai/agents/${id}/run`, { method: 'POST', body: { message } });
}

export interface ExecutionView {
  id: string;
  agent_id: string;
  status: string;
  initiator: { type: string; id: string | null };
  input: string;
  output: string | null;
  steps: number;
  failure_reason: string | null;
  started_at: string;
}

export function listExecutions(agentId?: string): Promise<{ executions: ExecutionView[] }> {
  return apiRequest(`/v1/ai/executions${agentId ? `?agentId=${agentId}` : ''}`);
}

export interface ExecutionDetail extends ExecutionView {
  version: { id: string; number: number; content_hash: string } | null;
  trace: {
    position: number;
    kind: string;
    name: string;
    input: Record<string, unknown>;
    output: Record<string, unknown>;
    failure_reason: string | null;
  }[];
}

export function getExecution(id: string): Promise<ExecutionDetail> {
  return apiRequest(`/v1/ai/executions/${id}`);
}

export interface AIUsage {
  executions: number;
  prompt_tokens: number;
  completion_tokens: number;
  cost_cents: number;
  models: { provider: string; model: string; tokens: number; cost_cents: number }[];
}

export function getUsage(): Promise<AIUsage> {
  return apiRequest('/v1/ai/usage');
}

// ---------------------------------------------------------------------------
// The knowledge base
// ---------------------------------------------------------------------------

export const KNOWLEDGE_STATUSES = ['PENDING', 'INDEXING', 'READY', 'FAILED', 'ARCHIVED'] as const;

export type KnowledgeStatus = (typeof KNOWLEDGE_STATUSES)[number];

export type KnowledgeKind = 'DOCUMENT' | 'FAQ' | 'PAGE' | 'FILE' | 'URL';

export interface KnowledgeSource {
  id: string;
  title: string;
  kind: KnowledgeKind;
  status: KnowledgeStatus;
  chunkCount: number;
  characters: number;
  tags: string[];
  embeddingModel: string | null;
  indexedAt: string | null;
  failureReason: string | null;
  ownerId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface KnowledgeDetail extends KnowledgeSource {
  content: string;
}

export function listKnowledge(
  query: {
    search?: string;
    status?: KnowledgeStatus[];
  } = {},
): Promise<{ sources: KnowledgeSource[] }> {
  const params = new URLSearchParams();

  if (query.search) params.set('search', query.search);
  for (const status of query.status ?? []) params.append('status', status);

  return apiRequest(`/v1/ai/knowledge?${params.toString()}`);
}

export function getKnowledge(id: string): Promise<KnowledgeDetail> {
  return apiRequest(`/v1/ai/knowledge/${id}`);
}

export function addKnowledge(payload: {
  title: string;
  content: string;
  kind?: KnowledgeKind;
  tags?: string[];
}): Promise<KnowledgeSource> {
  return apiRequest('/v1/ai/knowledge', { method: 'POST', body: payload });
}

export function editKnowledge(
  id: string,
  payload: { title?: string; content?: string; tags?: string[] },
): Promise<KnowledgeSource> {
  return apiRequest(`/v1/ai/knowledge/${id}`, { method: 'PATCH', body: payload });
}

export function reindexKnowledge(id: string): Promise<KnowledgeSource> {
  return apiRequest(`/v1/ai/knowledge/${id}/reindex`, { method: 'POST' });
}

export function removeKnowledge(id: string): Promise<void> {
  return apiRequest(`/v1/ai/knowledge/${id}`, { method: 'DELETE' });
}

export interface KnowledgeHit {
  chunkId: string;
  sourceId: string;
  title: string;
  heading: string | null;
  content: string;
  similarity: number;
}

export function searchKnowledge(query: string): Promise<{ hits: KnowledgeHit[] }> {
  return apiRequest('/v1/ai/knowledge/search', { method: 'POST', body: { query } });
}

/**
 * How an indexing state reads.
 *
 * `PENDING` is not a warning — it is the normal state for the thirty seconds before the poller picks
 * a document up, and colouring it amber would train people to ignore amber.
 */
export function knowledgeTone(status: KnowledgeStatus): BadgeTone {
  if (status === 'READY') return 'positive';
  if (status === 'FAILED') return 'danger';
  if (status === 'INDEXING') return 'info';

  return 'neutral';
}

// ---------------------------------------------------------------------------
// Batches: running an agent over many records
// ---------------------------------------------------------------------------

export const BATCH_STATUSES = ['PENDING', 'RUNNING', 'COMPLETED', 'FAILED', 'CANCELED'] as const;

export type BatchStatus = (typeof BATCH_STATUSES)[number];

export interface BatchProgress {
  total: number;
  done: number;
  failed: number;
  remaining: number;
  completion: number | null;
  spentCents: number;
}

export interface AgentBatch {
  id: string;
  agentId: string;
  agentName: string;
  name: string;
  status: BatchStatus;
  instruction: string;
  entityType: 'COMPANY' | 'CONTACT' | 'LEAD' | 'OPPORTUNITY';
  budgetCents: number | null;
  progress: BatchProgress;
  startedAt: string | null;
  finishedAt: string | null;
  failureReason: string | null;
  ownerId: string | null;
  createdAt: string;
}

export interface BatchItem {
  id: string;
  entityType: string;
  entityId: string;
  status: string;
  executionId: string | null;
  output: string | null;
  failureReason: string | null;
  finishedAt: string | null;
}

export interface BatchDetail extends AgentBatch {
  items: BatchItem[];
}

export function listBatches(): Promise<{ batches: AgentBatch[] }> {
  return apiRequest('/v1/ai/batches?limit=50');
}

export function getBatch(id: string): Promise<BatchDetail> {
  return apiRequest(`/v1/ai/batches/${id}`);
}

export function startBatch(payload: {
  agentId: string;
  name: string;
  instruction: string;
  entityType: string;
  budgetCents?: number | null;
}): Promise<AgentBatch> {
  return apiRequest('/v1/ai/batches', { method: 'POST', body: payload });
}

export function stopBatch(id: string): Promise<AgentBatch> {
  return apiRequest(`/v1/ai/batches/${id}/stop`, { method: 'POST' });
}

export function batchTone(status: BatchStatus): BadgeTone {
  if (status === 'COMPLETED') return 'positive';
  if (status === 'FAILED') return 'danger';
  if (status === 'RUNNING') return 'info';

  return 'neutral';
}

// ---------------------------------------------------------------------------
// Approvals (section 92) and the agents a tenant can start from
// ---------------------------------------------------------------------------

export type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED';

export interface Approval {
  id: string;
  executionId: string;
  agentId: string;
  agentName: string;
  toolName: string;
  arguments: Record<string, unknown>;
  status: ApprovalStatus;
  requestedOf: string | null;
  decidedBy: string | null;
  reason: string | null;
  requestedAt: string;
  decidedAt: string | null;
  expiresAt: string;
}

export function listApprovals(status?: ApprovalStatus[]): Promise<{ approvals: Approval[] }> {
  const params = new URLSearchParams();
  for (const value of status ?? []) params.append('status', value);

  return apiRequest(`/v1/ai/approvals?${params.toString()}`);
}

export function decideApproval(
  id: string,
  payload: { approved: boolean; reason?: string | null },
): Promise<{ approval: Approval; run: RunResult }> {
  return apiRequest(`/v1/ai/approvals/${id}/decide`, { method: 'POST', body: payload });
}

export interface AgentTemplate {
  key: string;
  name: string;
  role: string;
  description: string;
  instructions: string;
  tools: string[];
  approvalTools: string[];
  maxSteps: number;
}

export function listTemplates(): Promise<{ templates: AgentTemplate[] }> {
  return apiRequest('/v1/ai/templates');
}

export function createFromTemplate(key: string): Promise<AgentView> {
  return apiRequest('/v1/ai/agents/from-template', { method: 'POST', body: { key } });
}

/**
 * Importing what the product already holds into the knowledge base (Phase 143).
 *
 * One call per kind, because each refuses for its own reason: a page that is not published, a file
 * type nothing can read yet, a form with no questions.
 */
export function importKnowledgeFromPage(id: string): Promise<KnowledgeSource> {
  return apiRequest(`/v1/ai/knowledge/from-page/${id}`, { method: 'POST' });
}

export function importKnowledgeFromForm(id: string): Promise<KnowledgeSource> {
  return apiRequest(`/v1/ai/knowledge/from-form/${id}`, { method: 'POST' });
}

export function importKnowledgeFromFile(id: string): Promise<KnowledgeSource> {
  return apiRequest(`/v1/ai/knowledge/from-file/${id}`, { method: 'POST' });
}
